"use client";

import * as React from "react";
import { Link, useSearchParams } from "react-router";
import { Check, ChevronRight } from "lucide-react";

import { Badge } from "@/app/components/ui/badge";
import { Button } from "@/app/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/app/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/app/components/ui/toggle-group";
import { cn } from "@/app/components/ui/utils";
import {
  CCTV_ADDON_TIERS,
  MIN_LICENSES_FOR_SSO,
  MIN_LICENSES_MONTHLY_ENTERPRISE,
  MIN_LICENSES_MONTHLY_SAAS,
  MIN_LICENSES_YEARLY,
  pricingCardFlatFeatures,
  pricingCardSections,
  type PlanBillingCycle,
  type PlanCardSubsection,
  type PlanTier,
} from "@/app/data/planCatalog";
import { SESSION_AUDIO_MODES } from "@/app/data/audioCaptureContent";
import { MODULE_ADDON_SECTION_LABEL, MODULE_SKUS } from "@/app/data/moduleCatalog";
import { CAMERA_INDICATOR_DISCLOSURE, WEBCAM_CADENCE_MATRIX } from "@/app/data/webcamCaptureContent";
import { Seo } from "../components/Seo";

type PricingRegion = "US" | "IN";

const ESSENTIAL_MONTHLY_USD = 20;
const ESSENTIAL_MONTHLY_INR = 1900;
const MONTHLY_USD = 59;
const MONTHLY_INR = 4900;
/** Annual billing: exactly 30% off (pay 70% of list annual = monthly x 12). */
const YEARLY_DISCOUNT = 0.3;
const ENTERPRISE_USD_DELTA = 10;

function fetchWithTimeout(url: string, ms: number): Promise<Response> {
  const ctrl = new AbortController();
  const t = window.setTimeout(() => ctrl.abort(), ms);
  return fetch(url, { signal: ctrl.signal }).finally(() => window.clearTimeout(t));
}

/**
 * India vs US/global from visitor IP via ipwho.is, then free fallbacks (no API keys).
 * Primary: https://ipwho.is/
 */
async function detectPricingRegionFromIp(): Promise<PricingRegion> {
  try {
    const res = await fetchWithTimeout("https://ipwho.is/", 9000);
    if (res.ok) {
      const data = (await res.json()) as { success?: boolean; country_code?: string };
      if (data.country_code === "IN") return "IN";
      if (data.country_code) return "US";
    }
  } catch {
    /* try fallbacks */
  }

  try {
    const res = await fetchWithTimeout("https://ipapi.co/json/", 9000);
    if (res.ok) {
      const data = (await res.json()) as { country_code?: string; error?: boolean };
      if (!data.error && data.country_code === "IN") return "IN";
      if (data.country_code) return "US";
    }
  } catch {
    /* try fallbacks */
  }

  try {
    const res = await fetchWithTimeout("https://get.geojs.io/v1/ip/country.json", 9000);
    if (res.ok) {
      const data = (await res.json()) as { country?: string };
      const code = (data.country ?? "").trim().toUpperCase();
      if (code === "IN") return "IN";
      return "US";
    }
  } catch {
    /* default */
  }

  return "US";
}

/** `?cur=usd` / `?cur=inr` overrides IP-based currency for testing or sharing links. */
function regionFromCurParam(searchParams: URLSearchParams): PricingRegion | null {
  const raw = searchParams.get("cur")?.trim().toLowerCase() ?? "";
  if (raw === "usd" || raw === "us") return "US";
  if (raw === "inr" || raw === "in") return "IN";
  return null;
}

function curOverrideFromLocation(): PricingRegion | null {
  if (typeof window === "undefined") return null;
  return regionFromCurParam(new URLSearchParams(window.location.search));
}

function usePricingRegion(): {
  region: PricingRegion;
  regionReady: boolean;
  setCurrency: (next: PricingRegion) => void;
  isDetecting: boolean;
  detectError: boolean;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  /** Router params first; fall back to `window.location` so `?cur=` applies on the first paint. */
  const curOverride = React.useMemo(() => {
    const fromRouter = regionFromCurParam(searchParams);
    if (fromRouter !== null) return fromRouter;
    return curOverrideFromLocation();
  }, [searchParams]);

  const [geoRegion, setGeoRegion] = React.useState<PricingRegion | null>(null);
  const [detectError, setDetectError] = React.useState(false);

  const isDetecting = curOverride === null && geoRegion === null && !detectError;
  const regionReady = curOverride !== null || geoRegion !== null || detectError;
  const region: PricingRegion = curOverride ?? geoRegion ?? "US";

  const setCurrency = React.useCallback(
    (next: PricingRegion) => {
      setSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          nextParams.set("cur", next === "IN" ? "inr" : "usd");
          return nextParams;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  React.useEffect(() => {
    if (curOverride !== null) {
      setDetectError(false);
      return;
    }

    let cancelled = false;
    setGeoRegion(null);
    setDetectError(false);
    void (async () => {
      try {
        const r = await detectPricingRegionFromIp();
        if (cancelled) return;
        setGeoRegion(r);
        if (r === "IN") {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              if (!next.get("cur")) next.set("cur", "inr");
              return next;
            },
            { replace: true },
          );
        }
      } catch {
        if (!cancelled) setDetectError(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [curOverride, setSearchParams]);

  return {
    region,
    regionReady,
    setCurrency,
    isDetecting,
    detectError,
  };
}

function PricePlaceholder({ className }: { className?: string }) {
  return (
    <span
      className={cn("inline-block h-[1em] min-w-[5rem] animate-pulse rounded bg-slate-800 align-middle", className)}
      aria-hidden
    />
  );
}

/** Yearly = 30% off list annual: pay 70% of (monthly x 12). Per-month equivalent = monthly x 0.7. */
function yearlyAmountsExact(monthly: number) {
  const billedAnnual = monthly * 12 * (1 - YEARLY_DISCOUNT);
  const effectiveMonthly = monthly * (1 - YEARLY_DISCOUNT);
  return { billedAnnual, effectiveMonthly };
}

function enterpriseMonthlyAmount(region: PricingRegion): number {
  if (region === "US") return MONTHLY_USD + ENTERPRISE_USD_DELTA;
  return MONTHLY_INR + Math.floor((ENTERPRISE_USD_DELTA * MONTHLY_INR) / MONTHLY_USD);
}

function formatMoney(region: PricingRegion, amount: number): string {
  if (region === "US") {
    const int = Math.round(amount * 100) % 100 === 0;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: int ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Math.round(amount));
}

function getTierDisplayAmounts(region: PricingRegion, billing: PlanBillingCycle, monthly: number) {
  if (billing === "monthly") {
    return {
      headline: formatMoney(region, monthly),
      unit: "/month",
      compareAt: null as string | null,
      footnote: null as string | null,
    };
  }
  const { billedAnnual, effectiveMonthly } = yearlyAmountsExact(monthly);
  return {
    headline: formatMoney(region, effectiveMonthly),
    unit: "/month",
    compareAt: formatMoney(region, monthly),
    footnote: `${formatMoney(region, billedAnnual)} billed annually (30% off)`,
  };
}

function getEssentialDisplayAmounts(region: PricingRegion, billing: PlanBillingCycle) {
  const monthly = region === "US" ? ESSENTIAL_MONTHLY_USD : ESSENTIAL_MONTHLY_INR;
  return getTierDisplayAmounts(region, billing, monthly);
}

function getProDisplayAmounts(region: PricingRegion, billing: PlanBillingCycle) {
  const monthly = region === "US" ? MONTHLY_USD : MONTHLY_INR;
  return getTierDisplayAmounts(region, billing, monthly);
}

/** Per-camera add-on line in the CCTV table (one amount; yearly = 30% off list per month). */
function formatCctvPerCameraMo(
  region: PricingRegion,
  billing: PlanBillingCycle,
  listMonthly: number,
): string {
  const amount = billing === "monthly" ? listMonthly : listMonthly * (1 - YEARLY_DISCOUNT);
  return `${formatMoney(region, amount)}/camera/mo`;
}

function formatModulePerSeatMo(
  region: PricingRegion,
  billing: PlanBillingCycle,
  listUsd: number,
  listInr: number,
): string {
  const list = region === "US" ? listUsd : listInr;
  const amount = billing === "monthly" ? list : list * (1 - YEARLY_DISCOUNT);
  return `${formatMoney(region, amount)}/seat/mo`;
}

function getEnterpriseDisplayAmounts(region: PricingRegion, billing: PlanBillingCycle) {
  const monthly = enterpriseMonthlyAmount(region);
  if (billing === "monthly") {
    return {
      headline: formatMoney(region, monthly),
      unit: "/month",
      compareAt: null as string | null,
      footnote: null as string | null,
    };
  }
  const { billedAnnual, effectiveMonthly } = yearlyAmountsExact(monthly);
  return {
    headline: formatMoney(region, effectiveMonthly),
    unit: "/month",
    compareAt: formatMoney(region, monthly),
    footnote: `${formatMoney(region, billedAnnual)} billed annually · software subscription (30% off)`,
  };
}

function FeatureRow({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex gap-3 text-sm leading-relaxed">
      <Check className="mt-0.5 size-4 shrink-0 text-[#008080]" aria-hidden />
      <span className="text-slate-300">{children}</span>
    </li>
  );
}

function PlanTierPlusSubheading({ title }: { title: string }) {
  return (
    <li className="block list-none pt-1">
      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-teal-400">{title}</p>
    </li>
  );
}

const CARD_SUBSECTION_TITLES: Partial<Record<PlanCardSubsection, string>> = {
  plus: "Everything in Essential, plus",
  governance: "Governance and accountability",
  ai: "AI Intelligence",
  addons: "Optional add-ons (purchased separately)",
};

const ENTERPRISE_CARD_SUBSECTION_TITLES: Partial<Record<PlanCardSubsection, string>> = {
  plus: "Everything in Pro, plus",
  ai: "AI Intelligence",
  addons: "Optional add-ons (purchased separately)",
};

function TierFeatureList({ tier, billing }: { tier: PlanTier; billing: PlanBillingCycle }) {
  if (tier === "essential") {
    return (
      <ul className="space-y-3">
        {pricingCardFlatFeatures("essential", billing).map((label) => (
          <FeatureRow key={label}>{label}</FeatureRow>
        ))}
      </ul>
    );
  }

  const titles = tier === "enterprise" ? ENTERPRISE_CARD_SUBSECTION_TITLES : CARD_SUBSECTION_TITLES;
  const sections = pricingCardSections(tier, billing);

  return (
    <ul className="space-y-3">
      {sections.map(({ subsection, labels }) => (
        <React.Fragment key={subsection}>
          {titles[subsection] ? <PlanTierPlusSubheading title={titles[subsection]!} /> : null}
          {labels.map((label) => (
            <FeatureRow key={label}>{label}</FeatureRow>
          ))}
        </React.Fragment>
      ))}
    </ul>
  );
}

/** Pilot feature list (Managed SaaS scope; see paragraph in band). */
const trialFeatureLines = [
  "3 endpoint licenses (full fleet visibility)",
  "Unlimited Recording Hours",
  "Standard Definition capture (720p / 2 FPS)",
  "DeskReplay Dashboard",
];

/** Primary plan CTAs: same filled treatment as Enterprise (teal, reads clearly as a button on dark cards). */
const planTierCtaClassName =
  "w-full border border-teal-500/40 bg-[#008080] text-white shadow-md shadow-teal-900/25 transition-colors " +
  "hover:bg-[#006666] hover:shadow-lg hover:shadow-teal-900/30 active:bg-[#005858]";

/** Selected segment only: avoid unconditional bg on both items. */
const toggleItemClass =
  "rounded-md border border-transparent bg-transparent px-6 py-2.5 text-slate-400 transition-colors " +
  "hover:bg-slate-800/70 hover:text-slate-100 " +
  "data-[state=on]:border-teal-900/50 data-[state=on]:bg-[#0f4d48] data-[state=on]:text-white " +
  "data-[state=on]:shadow-inner data-[state=on]:shadow-teal-950/30";

export function Pricing() {
  const [billing, setBilling] = React.useState<PlanBillingCycle>("monthly");
  const { region, regionReady, setCurrency, isDetecting, detectError } = usePricingRegion();
  const essentialAmounts = getEssentialDisplayAmounts(region, billing);
  const proAmounts = getProDisplayAmounts(region, billing);
  const enterpriseAmounts = getEnterpriseDisplayAmounts(region, billing);
  const savingsLabel = billing === "yearly" ? "Save 30%" : "Yearly: 30% off";

  return (
    <>
      <Seo
        title="Pricing"
        description="Compare DeskReplay plans: Essential, Pro, and Enterprise. Review retention, capture quality (720p to 4K), AI intelligence features, and self-hosted deployment options."
        canonicalPath="/pricing"
      />
      <div className="min-h-screen bg-slate-950 pt-28 pb-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 max-w-3xl">
          <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-teal-400">Pricing</p>
          <h1 className="mb-4 text-4xl font-extrabold tracking-tight text-slate-100">
            Plans for baseline compliance, managed SaaS, and sovereign deployments
          </h1>
          <p className="text-lg leading-relaxed text-on-dark-muted">
            Start with Essential on DeskReplay-hosted infrastructure, scale with Pro, or run Enterprise on your own metal
            with full identity and retention control.{" "}
            <Link to="/pricing/compare" className="font-medium text-teal-400 hover:text-teal-300">
              Compare all features
            </Link>
            .
          </p>
        </div>

        <div className="mb-12 flex flex-col gap-3">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
            {/* Billing */}
            <div className="flex shrink-0 flex-wrap items-center gap-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Billing</span>
              <ToggleGroup
                type="single"
                value={billing}
                onValueChange={(v) => v && setBilling(v as PlanBillingCycle)}
                variant="outline"
                size="default"
                className="rounded-lg border border-slate-700 bg-slate-950/80 p-1"
              >
                <ToggleGroupItem value="monthly" className={toggleItemClass}>
                  Monthly
                </ToggleGroupItem>
                <ToggleGroupItem value="yearly" className={toggleItemClass}>
                  Yearly
                </ToggleGroupItem>
              </ToggleGroup>
              <Badge
                variant="outline"
                className={cn(
                  "border-teal-500/40 text-teal-300 hover:bg-teal-500/15",
                  billing === "yearly" ? "bg-teal-500/10" : "bg-slate-800/60",
                )}
              >
                {savingsLabel}
              </Badge>
            </div>

            {/* Currency */}
            <div className="flex shrink-0 flex-wrap items-center gap-3 sm:justify-end">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Currency</span>
              <ToggleGroup
                type="single"
                value={regionReady ? (region === "IN" ? "inr" : "usd") : ""}
                onValueChange={(v) => {
                  if (v === "usd") setCurrency("US");
                  if (v === "inr") setCurrency("IN");
                }}
                variant="outline"
                size="default"
                disabled={isDetecting}
                className="rounded-lg border border-slate-700 bg-slate-950/80 p-1"
              >
                <ToggleGroupItem value="usd" className={toggleItemClass}>
                  USD ($)
                </ToggleGroupItem>
                <ToggleGroupItem value="inr" className={toggleItemClass}>
                  INR (&#8377;)
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>

          <p className="text-sm leading-relaxed text-on-dark-dim max-w-3xl">
            {billing === "monthly" ? (
              <>
                Monthly billing requires at least {MIN_LICENSES_MONTHLY_SAAS} endpoint licenses on Essential and Pro,
                and {MIN_LICENSES_MONTHLY_ENTERPRISE} on Enterprise. Yearly billing starts at {MIN_LICENSES_YEARLY}{" "}
                endpoint licenses on any paid plan.
              </>
            ) : (
              <>
                Yearly billing starts at {MIN_LICENSES_YEARLY} endpoint licenses on Essential, Pro, and Enterprise.
                Enterprise SSO / OIDC requires at least {MIN_LICENSES_FOR_SSO} licenses regardless of billing cycle.
              </>
            )}
          </p>

          {(detectError || isDetecting) && (
            <p className="text-sm text-on-dark-dim">
              {detectError ? <>Could not detect location. Defaulting to USD.</> : <>Detecting your location...</>}
            </p>
          )}
        </div>

        <div className="grid gap-6 lg:grid-cols-3 lg:gap-8">
          {/* ESSENTIAL */}
          <Card
            id="essential"
            className="flex flex-col scroll-mt-28 border border-slate-800 bg-slate-900/60 shadow-none"
          >
            <CardHeader className="gap-2 space-y-0 pb-2">
              <Badge
                variant="outline"
                className="w-fit border-slate-600 bg-transparent text-xs font-semibold uppercase tracking-wider text-teal-400"
              >
                Managed SaaS
              </Badge>
              <CardTitle className="text-2xl font-bold text-slate-100">ESSENTIAL</CardTitle>
              <CardDescription className="text-base font-medium text-slate-200">
                Baseline Compliance
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col gap-6 pt-2">
              <div>
                <div className="flex flex-wrap items-baseline gap-2">
                  {regionReady && essentialAmounts.compareAt && (
                    <span className="text-lg font-medium text-slate-500 line-through decoration-slate-600">
                      {essentialAmounts.compareAt}
                    </span>
                  )}
                  <span className="text-3xl font-bold tracking-tight text-slate-100" aria-busy={!regionReady}>
                    {regionReady ? essentialAmounts.headline : <PricePlaceholder className="h-9 min-w-[6rem]" />}
                  </span>
                  <span className="text-on-dark-muted">{essentialAmounts.unit}</span>
                </div>
                {regionReady && essentialAmounts.footnote ? (
                  <p className="mt-1 text-sm text-on-dark-muted">{essentialAmounts.footnote}</p>
                ) : regionReady ? (
                  <p className="mt-1 text-sm text-on-dark-muted">Billed monthly · cancel anytime</p>
                ) : null}
              </div>
              <TierFeatureList tier="essential" billing={billing} />
              <p className="text-xs leading-relaxed text-on-dark-dim">
                <span className="font-medium text-slate-400">*</span> Basic OCR (on-screen text search) and Chat with Your
                Recordings are included from Pro. Advanced OCR (visual concept, image match, and policy alerts) is
                Enterprise.
              </p>
            </CardContent>
            <CardFooter className="mt-auto pt-2">
              <Button size="lg" className={planTierCtaClassName} asChild>
                <Link to="/request-demo?plan=essential">
                  Get Started with Essential
                  <ChevronRight className="size-4" />
                </Link>
              </Button>
            </CardFooter>
          </Card>

          {/* PRO */}
          <Card
            key={`pro-${billing}-${region}`}
            className="relative flex flex-col border border-slate-800 bg-slate-900/60 shadow-none ring-1 ring-slate-700/50"
          >
            <CardHeader className="gap-2 space-y-0 pb-2">
              <Badge
                variant="outline"
                className="w-fit border-teal-500/35 bg-teal-500/10 text-xs font-semibold uppercase tracking-wider text-teal-300"
              >
                Managed SaaS Plus
              </Badge>
              <CardTitle className="text-2xl font-bold text-slate-100">PRO</CardTitle>
              <CardDescription className="text-base font-medium text-slate-200">
                Managed Accountability
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col gap-6 pt-2">
              <div>
                <div className="flex flex-wrap items-baseline gap-2">
                  {regionReady && proAmounts.compareAt && (
                    <span className="text-lg font-medium text-slate-500 line-through decoration-slate-600">
                      {proAmounts.compareAt}
                    </span>
                  )}
                  <span className="text-3xl font-bold tracking-tight text-slate-100" aria-busy={!regionReady}>
                    {regionReady ? proAmounts.headline : <PricePlaceholder className="h-9 min-w-[6rem]" />}
                  </span>
                  <span className="text-on-dark-muted">{proAmounts.unit}</span>
                </div>
                {regionReady && proAmounts.footnote ? (
                  <p className="mt-1 text-sm text-on-dark-muted">{proAmounts.footnote}</p>
                ) : regionReady ? (
                  <p className="mt-1 text-sm text-on-dark-muted">Billed monthly · cancel anytime</p>
                ) : null}
              </div>
              <TierFeatureList tier="pro" billing={billing} />
              <p className="text-xs leading-relaxed text-on-dark-dim mt-3">
                500 GB included. Your own R2 or S3 bucket is optional.
              </p>
              <p className="text-xs leading-relaxed text-on-dark-dim">
                <span className="font-medium text-slate-400">*</span> BYOK (bring your own key): connect OpenAI, Google
                Gemini, Claude, DeepSeek, Grok, Azure OpenAI, or self-hosted Ollama with your organization's credentials.
              </p>
            </CardContent>
            <CardFooter className="mt-auto pt-2">
              <Button size="lg" className={planTierCtaClassName} asChild>
                <Link to="/request-demo?plan=pro">
                  Get Started with Pro
                  <ChevronRight className="size-4" />
                </Link>
              </Button>
            </CardFooter>
          </Card>

          {/* ENTERPRISE */}
          <Card
            key={`ent-${billing}-${region}`}
            className={cn(
              "flex flex-col border border-teal-600/30 bg-slate-900/60 shadow-[0_0_60px_-12px_rgba(0,128,128,0.35)]",
              "ring-1 ring-teal-500/25",
            )}
          >
            <CardHeader className="gap-2 space-y-0 pb-2">
              <Badge
                variant="outline"
                className="w-fit border-teal-500/40 bg-teal-500/15 text-xs font-semibold uppercase tracking-wider text-teal-200"
              >
                Self-Hosted / On-Prem
              </Badge>
              <CardTitle className="text-2xl font-bold text-slate-100">ENTERPRISE</CardTitle>
              <CardDescription className="text-base font-medium text-slate-200">
                Total Sovereignty
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col gap-6 pt-2">
              <div>
                <div className="flex flex-wrap items-baseline gap-2">
                  {regionReady && enterpriseAmounts.compareAt && (
                    <span className="text-lg font-medium text-slate-500 line-through decoration-slate-600">
                      {enterpriseAmounts.compareAt}
                    </span>
                  )}
                  <span className="text-3xl font-bold tracking-tight text-slate-100" aria-busy={!regionReady}>
                    {regionReady ? enterpriseAmounts.headline : <PricePlaceholder className="h-9 min-w-[6rem]" />}
                  </span>
                  <span className="text-on-dark-muted">{enterpriseAmounts.unit}</span>
                </div>
                {regionReady && enterpriseAmounts.footnote ? (
                  <p className="mt-1 text-sm text-on-dark-muted">{enterpriseAmounts.footnote}</p>
                ) : regionReady ? (
                  <p className="mt-1 text-sm text-on-dark-muted">
                    Billed monthly + one time setup cost · cancel anytime
                  </p>
                ) : null}
              </div>
              <TierFeatureList tier="enterprise" billing={billing} />
              <p className="text-xs leading-relaxed text-on-dark-dim">
                <span className="font-medium text-slate-400">*</span> Self-hosted: &quot;Unlimited storage&quot; is the
                capacity you provision (disks, object storage, SAN, and so on). Recordings stay in your environment.
              </p>
            </CardContent>
            <CardFooter className="mt-auto pt-2">
              <Button size="lg" className={planTierCtaClassName} asChild>
                <Link to="/request-demo?plan=enterprise">
                  Request Enterprise Briefing
                  <ChevronRight className="size-4" />
                </Link>
              </Button>
            </CardFooter>
          </Card>
        </div>

        <p className="mt-8 text-center">
          <Link
            to="/pricing/compare"
            className="inline-flex items-center gap-1 text-sm font-semibold text-teal-400 hover:text-teal-300"
          >
            Compare all features across Essential, Pro, and Enterprise
            <ChevronRight className="size-4" />
          </Link>
        </p>

        {/* 7-day free pilot: highlighted band + feature list (managed SaaS only). */}
        <div
          className={cn(
            "relative mt-14 overflow-hidden rounded-2xl px-6 py-8 md:px-10 md:py-10",
            "border-2 border-teal-400/50 bg-gradient-to-br from-teal-500/10 via-slate-900 to-slate-950",
            "shadow-[0_0_48px_-12px_rgba(45,212,196,0.45),inset_0_1px_0_rgba(255,255,255,0.06)]",
            "ring-1 ring-teal-300/25",
          )}
        >
          <div
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(45,212,196,0.12),transparent_55%)]"
            aria-hidden
          />
          <div className="relative flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between lg:gap-12">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <p className="text-xs font-semibold uppercase tracking-widest text-teal-300">7-day free pilot</p>
                <span className="hidden text-teal-400/50 sm:inline" aria-hidden>
                  ·
                </span>
                <Badge
                  variant="outline"
                  className="border-teal-500/40 bg-teal-500/10 px-3 py-1 text-xs font-semibold tracking-wide text-teal-300"
                >
                  No credit card required
                </Badge>
              </div>
              <h2 className="mt-2 text-xl font-bold tracking-tight text-slate-100 md:text-2xl">
                Try DeskReplay on managed SaaS before you commit
              </h2>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-teal-100/85">
                Full evaluation on our hosted stack (same SaaS platform as Essential and Pro). No surprise charges: we only
                ask for a work email to start.
                Self-hosted Enterprise is not included in this pilot, hosting your own infrastructure has real cost; use{" "}
                <Link
                  to="/request-demo?plan=enterprise"
                  className="font-medium text-teal-300 underline decoration-teal-300/70 underline-offset-2 hover:text-teal-200 hover:decoration-teal-200"
                >
                  Request Enterprise Briefing
                </Link>{" "}
                for on-prem scoping.
              </p>
              <Button size="lg" className={cn(planTierCtaClassName, "mt-6 w-full sm:w-auto")} asChild>
                <Link to="/get-started">
                  Start free pilot
                  <ChevronRight className="size-4" />
                </Link>
              </Button>
            </div>
            <div className="w-full shrink-0 lg:max-w-md lg:border-l lg:border-teal-500/25 lg:pl-10">
              <p className="text-xs font-semibold uppercase tracking-wider text-teal-200/90">What you get</p>
              <ul className="mt-4 space-y-3">
                {trialFeatureLines.map((line) => (
                  <FeatureRow key={line}>{line}</FeatureRow>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div
          className={cn(
            "mt-10 flex flex-col gap-4 rounded-2xl border border-slate-800 bg-slate-900/40 px-6 py-6 sm:flex-row sm:items-center sm:justify-between sm:gap-8 md:px-8",
          )}
        >
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-100">Bulk discounts</p>
            <p className="mt-1 max-w-xl text-sm leading-relaxed text-on-dark-muted">
              Need more than 50 licenses? We offer volume pricing across Essential, Pro, and Enterprise.
            </p>
          </div>
          <Button variant="outline" size="lg" className="shrink-0 border-teal-500/35 bg-teal-500/5 text-teal-300 hover:bg-teal-500/10 hover:text-teal-200" asChild>
            <Link to="/request-demo?plan=essential&seats=volume">
              Request a quote
              <ChevronRight className="size-4" />
            </Link>
          </Button>
        </div>

        <div className="mt-20 text-center max-w-2xl mx-auto">
          <p className="text-on-dark-muted text-sm leading-relaxed">
            Base plans cover endpoint seats, storage, and governance.{" "}
            <span className="text-slate-200 font-medium">Optional modules</span> below are purchased separately and
            layered on Pro or Enterprise endpoint pools.
          </p>
        </div>

        {/* ===== SESSION AUDIO ADD-ON ===== */}
        <div id="session-audio-addon" className="mt-16 scroll-mt-28">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 mb-4">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              <span className="text-emerald-300 text-xs font-semibold uppercase tracking-wider">Add-on</span>
            </div>
            <h2 className="text-2xl font-extrabold tracking-tight text-slate-100 mb-3">
              Session Audio: optional streaming modules
            </h2>
            <p className="text-on-dark-muted text-base max-w-2xl mx-auto leading-relaxed">
              {MODULE_ADDON_SECTION_LABEL}. Requires Pro or Enterprise endpoint seats. Not bundled in base plans.
            </p>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-slate-800">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-900/60">
                  <th className="text-left py-4 px-6 text-slate-400 font-semibold w-1/3">Feature</th>
                  {MODULE_SKUS.filter((s) => s.id.startsWith("audio-")).map((sku) => (
                    <th key={sku.id} className="text-center py-4 px-6 text-slate-100 font-bold">
                      <div className="text-base">{sku.headline}</div>
                      <div className="text-teal-400 font-semibold text-sm mt-0.5" aria-busy={!regionReady}>
                        {regionReady
                          ? formatModulePerSeatMo(region, billing, sku.listUsdPerMonth, sku.listInrPerMonth)
                          : "…"}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                <tr>
                  <td className="py-3.5 px-6 text-on-dark-muted">System audio streaming</td>
                  <td className="py-3.5 px-6 text-center text-slate-300">Included</td>
                  <td className="py-3.5 px-6 text-center text-slate-300">Included</td>
                </tr>
                <tr>
                  <td className="py-3.5 px-6 text-on-dark-muted">Microphone streaming</td>
                  <td className="py-3.5 px-6 text-center text-slate-300">—</td>
                  <td className="py-3.5 px-6 text-center text-slate-300">Included</td>
                </tr>
                <tr>
                  <td className="py-3.5 px-6 text-on-dark-muted">Summary</td>
                  <td className="py-3.5 px-6 text-center text-slate-300 text-xs leading-relaxed">
                    {SESSION_AUDIO_MODES.system.description.slice(0, 120)}…
                  </td>
                  <td className="py-3.5 px-6 text-center text-slate-300 text-xs leading-relaxed">
                    {SESSION_AUDIO_MODES.mic.description.slice(0, 120)}…
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-center text-on-dark-muted text-sm">
            <Link to="/faq#session-audio-addon" className="text-teal-400 hover:text-teal-300">
              Session Audio add-on FAQs <ChevronRight className="inline size-3.5 -mt-0.5" />
            </Link>
          </p>
        </div>

        {/* ===== WEBCAM CAPTURE ADD-ON ===== */}
        <div id="webcam-capture-addon" className="mt-20 scroll-mt-28">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-violet-400/30 bg-violet-400/10 mb-4">
              <div className="w-1.5 h-1.5 rounded-full bg-violet-400" />
              <span className="text-violet-300 text-xs font-semibold uppercase tracking-wider">Add-on</span>
            </div>
            <h2 className="text-2xl font-extrabold tracking-tight text-slate-100 mb-3">
              Webcam Capture: identity verification modules
            </h2>
            <p className="text-on-dark-muted text-base max-w-2xl mx-auto leading-relaxed">
              Timed JPEG snapshots paired with screen policies. {MODULE_ADDON_SECTION_LABEL}.
            </p>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-slate-800">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-900/60">
                  <th className="text-left py-4 px-6 text-slate-400 font-semibold w-1/3">Capability</th>
                  {MODULE_SKUS.filter((s) => s.id.startsWith("webcam-")).map((sku) => (
                    <th key={sku.id} className="text-center py-4 px-6 text-slate-100 font-bold">
                      <div className="text-base">{sku.headline}</div>
                      <div className="text-teal-400 font-semibold text-sm mt-0.5" aria-busy={!regionReady}>
                        {regionReady
                          ? formatModulePerSeatMo(region, billing, sku.listUsdPerMonth, sku.listInrPerMonth)
                          : "…"}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {WEBCAM_CADENCE_MATRIX.map((row) => (
                  <tr key={row.feature}>
                    <td className="py-3.5 px-6 text-on-dark-muted">{row.feature}</td>
                    <td className="py-3.5 px-6 text-center text-slate-300">{row.pro ? "Included" : "—"}</td>
                    <td className="py-3.5 px-6 text-center text-slate-300">{row.enterprise ? "Included" : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-6 rounded-xl border border-violet-400/25 bg-violet-400/5 p-5 max-w-3xl mx-auto">
            <p className="text-violet-100 text-sm leading-relaxed">{CAMERA_INDICATOR_DISCLOSURE}</p>
          </div>
          <p className="mt-4 text-center text-on-dark-muted text-sm">
            <Link to="/faq#webcam-capture-addon" className="text-teal-400 hover:text-teal-300">
              Webcam Capture add-on FAQs <ChevronRight className="inline size-3.5 -mt-0.5" />
            </Link>
          </p>
        </div>

        {/* ===== CCTV+ PLUGIN ADD-ON ===== */}
        <div id="cctv-plugin" className="mt-20 scroll-mt-28">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-teal-400/30 bg-teal-400/10 mb-4">
              <div className="w-1.5 h-1.5 rounded-full bg-teal-400" />
              <span className="text-teal-300 text-xs font-semibold uppercase tracking-wider">Add-on</span>
            </div>
            <h2 className="text-2xl font-extrabold tracking-tight text-slate-100 mb-3">
              CCTV+ Plugin: Add AI Surveillance to Pro or Enterprise
            </h2>
            <p className="text-on-dark-muted text-base max-w-2xl mx-auto leading-relaxed">
              Connect NVR streams, run real-time alerts from live camera feeds, and build a full forensic archive of
              every camera. Priced per camera per month, layered on top of your DeskReplay seat plan. Not available on
              Essential.
            </p>
          </div>

          {/* Feature comparison table */}
          <div className="overflow-x-auto rounded-2xl border border-slate-800">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-900/60">
                  <th className="text-left py-4 px-6 text-slate-400 font-semibold w-1/3">Feature</th>
                  <th className="text-center py-4 px-6 text-slate-100 font-bold">
                    <div className="text-base">CCTV+ Pro</div>
                    <div className="text-teal-400 font-semibold text-sm mt-0.5" aria-busy={!regionReady}>
                      {regionReady ? (
                        formatCctvPerCameraMo(
                          region,
                          billing,
                          region === "US"
                            ? CCTV_ADDON_TIERS[0]!.priceUsdMonthly
                            : CCTV_ADDON_TIERS[0]!.priceInrMonthly,
                        )
                      ) : (
                        <PricePlaceholder className="mx-auto h-4 min-w-[8rem]" />
                      )}
                    </div>
                  </th>
                  <th className="text-center py-4 px-6 text-slate-100 font-bold">
                    <div className="text-base">CCTV+ Enterprise</div>
                    <div className="text-teal-400 font-semibold text-sm mt-0.5" aria-busy={!regionReady}>
                      {regionReady ? (
                        formatCctvPerCameraMo(
                          region,
                          billing,
                          region === "US"
                            ? CCTV_ADDON_TIERS[1]!.priceUsdMonthly
                            : CCTV_ADDON_TIERS[1]!.priceInrMonthly,
                        )
                      ) : (
                        <PricePlaceholder className="mx-auto h-4 min-w-[8rem]" />
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {[
                  {
                    feature: "Max cameras",
                    pro: "Up to 32 cameras",
                    ent: "Unlimited cameras",
                  },
                  {
                    feature: "Hot path real-time detection",
                    pro: "Included",
                    ent: "Included",
                  },
                  {
                    feature: "Alert latency",
                    pro: "Under 2 seconds",
                    ent: "Under 2 seconds",
                  },
                  {
                    feature: "Real-time person detection (NVR streams)",
                    pro: "Included",
                    ent: "Included",
                  },
                  {
                    feature: "Sleeping guard / immobility detection",
                    pro: "Included",
                    ent: "Included",
                  },
                  {
                    feature: "Cold path chunk indexing (5-min segments)",
                    pro: "Included",
                    ent: "Included",
                  },
                  {
                    feature: "OCR text extraction from footage",
                    pro: "Included",
                    ent: "Included",
                  },
                  {
                    feature: "Visual search index",
                    pro: "Included",
                    ent: "Included",
                  },
                  {
                    feature: "AI narrative summaries per segment",
                    pro: "Included",
                    ent: "Included",
                  },
                  {
                    feature: "Cross-camera semantic search",
                    pro: "Included",
                    ent: "Included",
                  },
                  {
                    feature: "Forensic export (SHA-256 manifest)",
                    pro: "Up to 5 exports/month",
                    ent: "Unlimited exports",
                  },
                  {
                    feature: "CCTV profiles (per-camera AI config)",
                    pro: "Up to 5 profiles",
                    ent: "Unlimited profiles",
                  },
                  {
                    feature: "NVR integration (HTTP upload, S3/R2 watch prefix)",
                    pro: "Included",
                    ent: "Included",
                  },
                  {
                    feature: "Priority alert routing to admin panel",
                    pro: "Included",
                    ent: "Included",
                  },
                  {
                    feature: "Dedicated AI model configuration",
                    pro: "Default models",
                    ent: "Custom model deployment",
                  },
                  {
                    feature: "SLA for CCTV AI processing",
                    pro: "Standard",
                    ent: "99.9% uptime SLA",
                  },
                ].map((row) => (
                  <tr key={row.feature} className="hover:bg-slate-900/30 transition-colors">
                    <td className="py-3.5 px-6 text-on-dark-muted">{row.feature}</td>
                    <td className="py-3.5 px-6 text-center text-slate-300">{row.pro}</td>
                    <td className="py-3.5 px-6 text-center text-slate-300">{row.ent}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Callout box */}
          <div className="mt-6 rounded-xl border border-amber-400/25 bg-amber-400/5 p-5 flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-amber-400/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <div className="w-2 h-2 rounded-full bg-amber-400" />
            </div>
            <div>
              <p className="text-amber-300 text-sm font-semibold mb-1">Plan requirements</p>
              <p className="text-amber-100/70 text-sm leading-relaxed">
                CCTV+ is not available on Essential. CCTV+ Pro requires DeskReplay Pro. CCTV+ Enterprise requires
                DeskReplay Enterprise. The add-on is billed per camera per month alongside your base seat plan.
              </p>
            </div>
          </div>

          {/* Annual discount note */}
          <div className="mt-4 text-center">
            <p className="text-on-dark-muted text-sm">
              Annual billing saves 30% on the CCTV+ add-on (same discount as base plans).{" "}
              <Link to="/faq#cctv-plugin" className="text-teal-400 hover:text-teal-300">
                Read CCTV+ Plugin FAQs <ChevronRight className="inline size-3.5 -mt-0.5" />
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-20 rounded-2xl border border-slate-800 bg-slate-900/30 p-8 lg:p-10">
          <h2 className="mb-4 text-xl font-bold text-slate-100">Notes on licensing</h2>
          <ul className="list-outside list-disc space-y-3 pl-6 text-sm leading-relaxed text-on-dark-muted marker:text-slate-500">
            <li>
              Limits for seats, storage, and APIs come from your quote or checkout. Fair-use caps may adjust over time
              where your agreement allows.
            </li>
            <li>
              Enterprise self-hosted sizing is worked out with your infrastructure team during architecture review.
            </li>
            <li>
              On-premises and self-hosted deployments have two parts: the software subscription, and one-time professional
              services. Installation, environment setup, and go-live support are scoped for your stack, quoted separately, and
              approved before we begin work.
            </li>
            <li>
              Billing, taxes, currency, and renewals are covered in the{" "}
              <Link to="/legal/pricing-policy" className="text-teal-400 hover:text-teal-300">
                Pricing Policy
              </Link>
              . Refund rules are in the{" "}
              <Link to="/legal/refund-policy" className="text-teal-400 hover:text-teal-300">
                Refund Policy
              </Link>
              .
            </li>
          </ul>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link
              to="/pricing/compare"
              className="inline-flex items-center gap-1 text-sm font-medium text-teal-400 hover:text-teal-300"
            >
              Compare all features <ChevronRight size={14} />
            </Link>
            <Link
              to="/faq#plans"
              className="inline-flex items-center gap-1 text-sm font-medium text-teal-400 hover:text-teal-300"
            >
              Read plan FAQs <ChevronRight size={14} />
            </Link>
            <Link to="/resources" className="text-sm text-on-dark-muted hover:text-teal-400">
              White Papers &amp; Resources
            </Link>
          </div>
        </div>
      </div>
      </div>
    </>
  );
}
